import {
  AmountInput,
  Input,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
} from "@salestrak/ui";
import { FILTER_MEMBER_STATUSES } from "#app/lib/role-utils";
import {
  UserSelector,
  AssigneeSelector,
  TeamSelector,
  RoleSelector,
  WorkflowSelector,
} from "#app/components/entity-selectors";
import type { FilterFieldType } from "#app/lib/filters/types";
import type { IssueScope } from "#app/lib/filters/issue-scope";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { FieldDatePicker } from "#app/components/custom-fields/field-date-picker";
import {
  moneyInputProps,
  numberInputProps,
  toNumericValue,
} from "#app/lib/custom-field-utils";
// Value inputs are glyph-free: an advanced row's field select already names
// the filter, and the value control sits after its operator.
//
// This is the ADVANCED builder's value control. The Simple panel does not come
// through here — its rows drill into `QuickOptionList`, built off the same
// options index — so there is one control per type and no variant to tune.
import { CustomFieldOptionInput } from "./inputs/custom-field-option-input";
import { DateInput } from "./inputs/date-input";
import { FilterEntitySelectInput } from "./inputs/filter-entity-select-input";
import type { Omittable } from "@salestrak/ui";
type FilterValueInputProps = Omittable<{
  fieldType: FilterFieldType;
  /** The workspace field a `custom-field` row filters — routes the editor
   *  per custom-field type (option picker, member picker, number, date…). */
  customField?: CustomFieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  multiple?: boolean;
  /** The screen's scope — narrows the entity pickers whose values the scope
   *  already bounds (stages to the scoped workflows'). */
  scope?: IssueScope;
}>;
const BOOLEAN_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

/**
 * The value control an advanced filter row renders after its operator, routed
 * on the field's type. Entity types open their respective EntitySelector.
 */
export function FilterValueInput({
  fieldType,
  customField,
  value,
  onChange,
  scope,
}: FilterValueInputProps) {
  // Every filter control sits at the 28px `sm` step — advanced rows put an
  // entity trigger beside the field/operator selects, and mixed control sizes
  // in one row read as a glitch.
  const selectSize = "sm";
  const inputSize = "sm";

  switch (fieldType) {
    case "string":
    case "email":
      return (
        <Input
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder={fieldType === "email" ? "Enter email..." : "Enter text..."}
          size={inputSize}
        />
      );

    case "number":
      return (
        <Input
          value={value != null ? String(value) : ""}
          onChange={(event) => {
            const text = event.target.value;
            const num = Number(text);
            onChange(text === "" ? null : Number.isNaN(num) ? text : num);
          }}
          placeholder="Enter number..."
          inputMode="numeric"
          size={inputSize}
        />
      );

    case "boolean": {
      const boolStr = value === true ? "true" : value === false ? "false" : null;

      return (
        <Select
          items={BOOLEAN_OPTIONS}
          value={boolStr}
          onValueChange={(next: string | null) =>
            onChange(next === "true" ? true : next === "false" ? false : null)
          }
        >
          <SelectTrigger size={selectSize}>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectPopup>
            {BOOLEAN_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <Text>{opt.label}</Text>
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
      );
    }

    case "date":
      return <DateInput value={value} onChange={onChange} />;

    case "entity-user":
      return (
        <UserSelector
          value={value as string[] | null}
          onValueChange={(ids) => onChange(ids.length > 0 ? ids : null)}
          size={selectSize}
          chips
          contentFit
          placeholder="Search members..."
          statuses={FILTER_MEMBER_STATUSES}
        />
      );

    case "entity-assignee":
      return (
        <AssigneeSelector
          value={value as string[] | null}
          onValueChange={(ids) => onChange(ids.length > 0 ? ids : null)}
          size={selectSize}
          chips
          contentFit
          placeholder="Search members..."
          allowSetPrimary={false}
          showPrimarySubtext={false}
          statuses={FILTER_MEMBER_STATUSES}
        />
      );

    case "entity-team":
      return (
        <TeamSelector
          value={value as string[] | null}
          onValueChange={(ids) => onChange(ids.length > 0 ? ids : null)}
          size={selectSize}
          chips
          contentFit
          placeholder="Search teams..."
        />
      );

    case "role":
      return (
        <RoleSelector
          value={value ? String(value) : null}
          onValueChange={(id) => onChange(id ? [id] : null)}
          size={selectSize}
          chips
          placeholder="Select role..."
        />
      );

    case "issue-type":
      return (
        <FilterEntitySelectInput
          type="issue-type"
          value={value}
          onChange={onChange}
          poolLabel="Project Types"
          placeholder="Select Project types..."
          searchPlaceholder="Find type..."
          emptyMessage="No matching Project types"
        />
      );

    case "workflow":
      return (
        <WorkflowSelector
          value={value as string[] | null}
          onValueChange={(ids) => onChange(ids.length > 0 ? ids : null)}
          maxSelections={0}
          size={selectSize}
          chips
          contentFit
          placeholder="Select workflows..."
        />
      );

    case "stage":
      return (
        <FilterEntitySelectInput
          type="stage"
          value={value}
          onChange={onChange}
          scope={scope}
          poolLabel="Stages"
          placeholder="Select stages..."
          searchPlaceholder="Search stages..."
          emptyMessage="No stages found"
        />
      );

    // A handful of named states, on one line after the operator.
    case "age-status":
      return (
        <FilterEntitySelectInput
          type="age-status"
          value={value}
          onChange={onChange}
          poolLabel="Follow up status"
          placeholder="Select status..."
          searchPlaceholder="Search statuses..."
          emptyMessage="No statuses found"
        />
      );

    // `workflow-stage`, `last-updated` and `more-options` are Simple-panel
    // shapes — they name no advanced field, so no row ever routes here for
    // them and they fall through to the text input below.

    // Custom-field rows route on the workspace field's own type. Everything
    // that already has an editor above re-enters through it — only the option
    // types get their dedicated picker.
    case "custom-field": {
      if (!customField) return null;
      switch (customField.type) {
        case "select":
        case "labels":
          return (
            <CustomFieldOptionInput
              field={customField}
              value={value}
              onChange={onChange}
            />
          );
        case "member":
          return (
            <FilterValueInput
              fieldType="entity-user"
              value={value}
              onChange={onChange}
            />
          );
        // The same formatted editor the cell uses, so a filter value is
        // typed the way the value it matches is shown.
        case "number":
          return (
            <AmountInput
              {...numberInputProps(customField.config)}
              aria-label={customField.name}
              value={toNumericValue(value)}
              onValueChange={onChange}
              size={inputSize}
            />
          );
        case "money":
          return (
            <AmountInput
              {...moneyInputProps(customField.config?.currency)}
              aria-label={customField.name}
              value={toNumericValue(value)}
              onValueChange={onChange}
              size={inputSize}
            />
          );
        // The field's own picker, so the operand takes the stored shape: a
        // "YYYY-MM-DD" day for a calendar-day field, an instant when the
        // field carries its time.
        case "date":
          return (
            <FieldDatePicker
              field={customField}
              value={value}
              onValueChange={onChange}
              placeholder="Select date..."
              size={inputSize}
            />
          );
        case "checkbox":
          return (
            <FilterValueInput
              fieldType="boolean"
              value={value}
              onChange={onChange}
            />
          );
        // Text-like types (text/email/url/paragraph). Presence-only types
        // never reach here — their operators all carry input: "none".
        default:
          return (
            <FilterValueInput
              fieldType="string"
              value={value}
              onChange={onChange}
            />
          );
      }
    }

    case "select":
    case "multi-select":
    default:
      return (
        <Input
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value || null)}
          placeholder="Enter value..."
          size={inputSize}
        />
      );
  }
}
